import { Pagination } from 'app/types/pagination'
import { gql } from 'graphql-request'

export const commentFragments = gql`
  fragment fullNameUser on User {
    fullName
    profileImage {
      url
      sizes {
        avatar {
          url
        }
      }
    }
  }

  fragment fullNameMember on Member {
    id
    fullName
    profileImage {
      url
      sizes {
        avatar {
          url
        }
      }
    }
    tier {
      name
    }
  }

  fragment comment on Comment {
    id
    content
    deleted
    likes
    hasLiked
    hasBlocked
    hasEdited

    createdAt

    member {
      value {
        ...fullNameUser
        ...fullNameMember
      }
    }
  }

  fragment childCommentFragment on Comment {
    ...comment
    replies {
      ...comment
      replies {
        ...comment
      }
    }
  }

  fragment commentFragment on Comment {
    ...comment
    replies {
      ...childCommentFragment
    }
  }
`

export const createCommentsQuery = () => gql`
  ${commentFragments}
  query getComments(
    $slug: String!
    $limit: Int!
    $page: Int!
    $sort: CommentSortEnum!
  ) {
    commentsBySlug(slug: $slug, sort: $sort, limit: $limit, page: $page) {
      totalDocs
      page
      docs {
        ...commentFragment
      }
    }
  }
`

export const createUserCommentsQuery = () => gql`
  ${commentFragments}
  query getComments($limit: Int!, $page: Int!) {
    userComments(limit: $limit, page: $page) {
      totalDocs
      page
      docs {
        ...commentFragment
        parentContent {
          slug
          title
        }
      }
    }
  }
`

export const createCommentQuery = () => gql`
  ${commentFragments}
  query getComment($id: String!) {
    Comment(id: $id) {
      ...commentFragment
    }
  }
`

export interface Comment {
  id: string
  memberName: string
  memberId: string
  profileImage: string
  tier: string
  content: string
  deleted: boolean
  blocked: boolean
  hasLiked: boolean
  hasEdited: boolean
  likes: number
  replies: Comment[]
  createdAt: string
  updatedAt?: string
  contentSlug?: string
  contentTitle?: string
}

export const mapSingle = (content: any) => {
  const member = content.member?.value
  return {
    id: content.id || '',
    memberName: member?.fullName || '',
    tier: member?.tier?.name || null,
    memberId: member?.id || '',
    content: content.content || '',
    deleted: content.deleted || false,
    blocked: content.hasBlocked || false,
    hasLiked: content.hasLiked || false,
    hasEdited: content.hasEdited || false,
    likes: content.likes || 0,
    replies: (content.replies || []).map(mapSingle).filter(filterComment),
    createdAt: content.createdAt,
    updatedAt: content.updatedAt || null,
    profileImage:
      member?.profileImage?.sizes?.avatar?.url ||
      member?.profileImage?.url ||
      '',
    contentSlug: content.parentContent?.slug || '',
    contentTitle: content?.parentContent?.title || '',
  }
}

export const filterComment = (comment: Comment) => {
  if (comment.blocked) {
    return false
  }

  if (comment.deleted) {
    return (
      comment.replies.length > 0 &&
      comment.replies.some((reply) => !reply.deleted)
    )
  }

  return true
}

export const mapToComments = (doc: any): Pagination<Comment> => {
  const data = doc?.commentsBySlug || doc?.userComments
  const comments = data?.docs || []
  if (!comments) return { total: 0, page: 0, docs: [] }

  return {
    total: data?.totalDocs || 0,
    page: data?.page || 0,
    docs: comments.map(mapSingle),
  }
}

export const mapToComment = (doc: any): Comment => {
  return mapSingle(doc?.Comment)
}
